import config from '../config';

// Kept separate from the consigner session ("auth") so signing one out never affects the other
const STORAGE_KEY = 'adminAuth';

export class AdminAuthError extends Error {
  constructor(message) {
    super(message);
    this.name = 'AdminAuthError';
  }
}

export function saveAdminSession({ username, token, expiresAt }, storage = window.localStorage) {
  storage.setItem(STORAGE_KEY, JSON.stringify({ username, token, expiresAt }));
}

export function clearAdminSession(storage = window.localStorage) {
  storage.removeItem(STORAGE_KEY);
}

/**
 * The signed-in admin, or null when there is no session or it has expired
 */
export function getAdminSession(storage = window.localStorage, now = Date.now()) {
  let session;
  try {
    session = JSON.parse(storage.getItem(STORAGE_KEY));
  } catch (err) {
    clearAdminSession(storage);
    return null;
  }

  if (!session || !session.token || !(new Date(session.expiresAt).getTime() > now)) {
    if (session) {
      clearAdminSession(storage);
    }
    return null;
  }
  return session;
}

/**
 * fetch() for admin API routes. Adds the admin token, JSON-encodes object bodies,
 * and signs the admin out when the API rejects the token.
 */
export async function adminFetch(path, options = {}, { fetchImpl = fetch, storage = window.localStorage, baseUrl = config.API_ENDPOINT } = {}) {
  const session = getAdminSession(storage);
  if (!session) {
    throw new AdminAuthError('Please sign in');
  }

  const headers = { ...(options.headers || {}), Authorization: `Bearer ${session.token}` };
  let body = options.body;
  if (body !== undefined && typeof body !== 'string') {
    body = JSON.stringify(body);
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetchImpl(`${baseUrl}${path}`, { ...options, headers, body });

  if (response.status === 401 || response.status === 403) {
    clearAdminSession(storage);
    throw new AdminAuthError('Your admin session has ended. Please sign in again.');
  }
  return response;
}

export async function loginAdmin(username, password, { fetchImpl = fetch, storage = window.localStorage, baseUrl = config.API_ENDPOINT } = {}) {
  const response = await fetchImpl(`${baseUrl}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password })
  });

  if (response.status === 401 || response.status === 400) {
    throw new Error('Invalid username or password');
  }
  if (!response.ok) {
    throw new Error('Unable to log in right now');
  }

  const { username: signedInAs, token, expiresAt } = await response.json();
  const session = { username: signedInAs, token, expiresAt };
  saveAdminSession(session, storage);
  return session;
}
